import { X, type LucideIcon } from "lucide-react";
import { cn } from "../../utils";
import { Button } from "../../ui/button";
import {
  ACTION_BORDER_CLASS,
  CARD_SENTIMENT_CLASS,
  DEFAULT_SENTIMENT_ICON,
} from "./consts";
import type { ToastProps } from "./types";

/**
 * Presentational notification card — the visual source of truth for toasts.
 * Rendered on its own (Storybook, docs) and by the `toast` helpers via sonner.
 */
export const Toast = ({
  title,
  description,
  sentiment = "default",
  icon,
  actions,
  onClose,
  className,
}: ToastProps) => {
  const hasDescription = !!description;
  const LeadingIcon: LucideIcon | null =
    icon === null ? null : (icon ?? DEFAULT_SENTIMENT_ICON[sentiment]);
  const actionButtons = (actions ?? []).slice(0, 2);

  const actionsGroup = actionButtons.length > 0 && (
    <div className="flex items-start gap-2">
      {actionButtons.map((action, index) => (
        <Button
          key={`${index}-${action.label}`}
          variant="outline"
          size="sm"
          onClick={action.onClick}
          className={ACTION_BORDER_CLASS[sentiment]}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );

  return (
    <div
      className={cn(
        "flex w-[360px] flex-col overflow-clip rounded-md border shadow-lg",
        CARD_SENTIMENT_CLASS[sentiment],
        className,
      )}
    >
      <div className="flex min-h-[46px] items-center gap-2 py-2.5 pl-4 pr-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {LeadingIcon && (
            <LeadingIcon className="size-4 shrink-0 text-icon-panel-neutral-mid" />
          )}
          <p className="min-w-0 flex-1 break-words text-body font-medium text-text-panel-neutral-strongest">
            {title}
          </p>
          {!hasDescription && actionsGroup}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon-sm"
            icon={X}
            onClick={onClose}
            aria-label="Close"
            className="text-icon-panel-neutral-mid"
          />
        )}
      </div>

      {hasDescription && (
        <div className="flex px-10 pb-3">
          <p className="min-w-0 flex-1 text-body font-normal text-text-panel-neutral-mid">
            {description}
          </p>
        </div>
      )}

      {hasDescription && actionButtons.length > 0 && (
        <div className="flex flex-col items-end justify-center pb-3 pl-10 pr-4">
          {actionsGroup}
        </div>
      )}
    </div>
  );
};
